// @ts-check

/** @file AwesomeTasks control-plane catalog bootstrap through generic stable-cursor pull. */

import SyncApiClient from "velocious/build/src/sync/sync-api-client.js"

import Option from "../models/option.js"
import Project from "../models/project.js"
import User from "../models/user.js"
import {flushLocalDatabaseWrites} from "../shared/local-database-persistence.js"
import {getSessionTokenSync} from "../shared/session-option.js"
import {projectCatalogCursorKey} from "./project-catalog-key.js"
import {ProjectSyncTransportError} from "./project-sync-error.js"

export {projectCatalogCursorKey} from "./project-catalog-key.js"

const DEFAULT_CATALOG_BATCH_SIZE = 100

/**
 * Pulls and durably applies the authenticated Project/User catalog.
 * @param {{authenticationToken?: string | null, batchSize?: number, postChanges?: (body: Record<string, unknown>, options?: {signal?: AbortSignal}) => Promise<Record<string, unknown>>, signal?: AbortSignal, userId: string}} args Inputs.
 * @returns {Promise<void>} Stable-cursor catalog completion.
 * @rejects {Error} When the session is absent or catalog sync fails.
 */
export async function syncProjectCatalog({authenticationToken = getSessionTokenSync(), batchSize = DEFAULT_CATALOG_BATCH_SIZE, postChanges = postCatalogChanges, signal, userId}) {
  if (typeof userId !== "string" || userId.length === 0) throw new Error("Project catalog sync requires a user id")
  // Same classified contract as the sync transport seam: an absent session is an
  // authentication transition the reporting seam suppresses, not a sync failure.
  if (!authenticationToken) throw new ProjectSyncTransportError({code: "authentication-required", retryable: false})

  await SyncApiClient.singleFlight(`awesome-tasks-catalog:${userId}`, async () => {
    await SyncApiClient.pullChangesWithCursor({
      authenticationToken,
      batchSize,
      cursorKey: projectCatalogCursorKey(userId),
      cursorModel: Option,
      postChanges,
      resources: {
        Project: {attributes: catalogAttributes, enabled: true, modelClass: Project},
        User: {attributes: catalogAttributes, enabled: true, modelClass: User}
      },
      signal
    })
    await flushLocalDatabaseWrites()
  })
}

/**
 * Removes server-only routing facts before catalog assignment.
 * @param {{data: Record<string, unknown>}} args Apply arguments.
 * @returns {Record<string, unknown>} Attributes safe for local catalog assignment.
 */
function catalogAttributes({data}) {
  const {syncScope: _syncScope, ...attributes} = data

  return attributes
}

/**
 * Posts one V2 catalog page through the app's authenticated HTTP seam.
 * @param {Record<string, unknown>} body Cursor page request.
 * @param {{signal?: AbortSignal}} [options] Cancellation state.
 * @returns {Promise<Record<string, unknown>>} Validated catalog page.
 */
async function postCatalogChanges(body, options) {
  const {authenticatedSyncPost} = await import("../database/project-replica-transport.js")
  const response = await authenticatedSyncPost("/velocious/sync/catalog/changes", {
    ...body,
    pendingMutationCount: 0,
    protocolVersion: 2
  }, options)

  return /** @type {Record<string, unknown>} */ (await response.json())
}
